import React, { ChangeEvent, useCallback, useEffect, useState } from "react";

import NextImage from "next/image";
import FileIcon from "public/icons/file-icon.svg";
import SearchIcon from "public/icons/search-icon.svg";
import SendIcon from "public/icons/send-icon.svg";
import LoaderGif from "public/loader.gif";

import { Box, Theme, Tooltip, useMediaQuery } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { RemoveAttachesApi, UploadAttachApi } from "@/api/mailbox";
import { isError, isSuccess } from "@/api/mailbox/helpers/predicates";
import { useSendLetter } from "@/api/mailbox/hooks/useSendLetter";
import { UploadAttachResponseDTOSuccess } from "@/api/mailbox/models";
import { SmallLadyProfileApi } from "@/api/profiles/";
import { Button } from "@/components/form/Button/Button";
import { ButtonWithIcon } from "@/components/form/Button/ButtonWithIcon";
import { TextField } from "@/components/form/text-field/TextField";
import { ModalCloseCross } from "@/components/modal/components/ModalCloseCross";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { StaticConst } from "@/constants/staticConst";
import { useFormFactory } from "@/hooks/useFormFactory";
import { LadyMinV2DTO } from "@/models/lady-dtos";
import { scrollBarSxStyles } from "@/shared/styles";

import { AutoResizeTextArea } from "../components/autoResizeTextArea";
import { CustomSelect } from "../components/mailboxSelect";
import { mapFormToQuery } from "../helpers/mapFormToQuery";
import { CreateLetterFormSchema } from "../models/CreateLetterForm.schema";

type CreateLetterFormProps = {
  ladyId?: number;
  ladyDto?: LadyMinV2DTO;
  isLightVersion?: boolean;
  defaultLetterText?: string;
  onSubmitSuccess?: () => void;
};

const STORAGE_KEY = "letter_drafts";

export const CreateLetterForm: React.FC<CreateLetterFormProps> = ({
  ladyId,
  ladyDto,
  isLightVersion,
  defaultLetterText,
  onSubmitSuccess,
}) => {
  const downXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { openModal, removeOne } = useOpenModal();
  const [isLoading, setIsLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [ladyDtoState, setLadyDtoState] = useState<LadyMinV2DTO | undefined>(
    ladyDto,
  );
  const [filePreviews, setFilePreviews] = useState<string[]>([]);

  const [smallLadyProfiles, setSmallLadyProfiles] = useState<LadyMinV2DTO[]>();
  const [attaches, setAttaches] = useState<UploadAttachResponseDTOSuccess[]>(
    [],
  );

  const [selectedLadyId, setSelectedLadyId] = useState<number | undefined>(
    ladyId,
  );
  const queryClient = useQueryClient();
  const { mutate: sendLetter } = useSendLetter(
    isLightVersion ? false : true,
    selectedLadyId,
  );
  const [searchTerm, setSearchTerm] = useState<number>();
  const [letterText, setLetterText] = useState("");

  const saveDraftForLady = (ladyId: number, text: string) => {
    if (!ladyId) return;

    try {
      const draftsJson = localStorage.getItem(STORAGE_KEY) || "{}";
      const drafts = JSON.parse(draftsJson);

      // Сохраняем под ID леди
      drafts[ladyId] = text;

      localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
    } catch (error) {
      console.error("Error saving draft to localStorage:", error);
    }
  };

  const getDraftForLady = (ladyId: number) => {
    if (!ladyId) return null;

    try {
      const draftsJson = localStorage.getItem(STORAGE_KEY) || "{}";
      const drafts = JSON.parse(draftsJson);

      return drafts[ladyId] || "";
    } catch (error) {
      console.error("Error retrieving draft from localStorage:", error);
      return "";
    }
  };

  const clearDraftForLady = useCallback((ladyId: number) => {
    if (!ladyId) return;

    try {
      const draftsJson = localStorage.getItem(STORAGE_KEY) || "{}";
      const drafts = JSON.parse(draftsJson);

      delete drafts[ladyId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
    } catch (error) {
      console.error("Error clearing draft from localStorage:", error);
    }
  }, []);

  const { register, onSubmitHandler, getValues, setValue } = useFormFactory({
    schema: CreateLetterFormSchema,
    onError: (errors) => {
      const firstError = Object.values(errors)[0];
      if (firstError?.message) {
        openModal("INFORMATION_MODAL", {
          message: firstError.message,
          statusReg: "fail",
        });
      }
    },
    mutate: (formData) => {
      createLetterWithMap(formData);
    },
  });

  const [resetCount, setResetCount] = useState(0);

  useEffect(() => {
    if (selectedLadyId) {
      const savedText = getDraftForLady(selectedLadyId);

      if (savedText && (!defaultLetterText || defaultLetterText === "")) {
        setLetterText(savedText);
        setValue("letterText", savedText);
      } else if (defaultLetterText) {
        setLetterText(defaultLetterText);
        setValue("letterText", defaultLetterText);
      } else {
        setLetterText("");
        setValue("letterText", "");
      }
    }
  }, [selectedLadyId, defaultLetterText, isLightVersion, setValue, resetCount]);

  const resetForm = useCallback(() => {
    setLetterText("");
    setValue("letterText", "");
    setFilePreviews([]);
    setAttaches([]);
    setUploadedFileNames([]);
    if (selectedLadyId) {
      clearDraftForLady(selectedLadyId);
    }

    setResetCount((prev) => prev + 1);
  }, [selectedLadyId, setValue, clearDraftForLady]);

  const notifySuccess = useCallback(() => {
    onSubmitSuccess?.();
    setTimeout(() => {
      queryClient.invalidateQueries(["MAIL_LIST"]);
    }, 300);
  }, [onSubmitSuccess, queryClient]);

  const createLetterWithMap = useCallback(
    (formData: CreateLetterFormSchema) => {
      if (!selectedLadyId) {
        openModal("INFORMATION_MODAL", {
          message: "Please, select a lady profile.",
          statusReg: "fail",
        });
        return false;
      }

      sendLetter(mapFormToQuery(formData, attaches), {
        onSuccess: () => {
          resetForm();
          notifySuccess();
        },
        onError: (error) => {
          console.error("Exception sending letter:", error);
        },
      });

      return true;
    },
    [selectedLadyId, openModal, sendLetter, attaches, resetForm, notifySuccess],
  );

  const getLastContacts = useCallback(async () => {
    if (smallLadyProfiles === undefined) {
      try {
        const profiles = await SmallLadyProfileApi.getLastContacts();
        setSmallLadyProfiles(profiles);
      } catch (error) {
        console.error("Failed to fetch lady profiles", error);
      }
    }
  }, [smallLadyProfiles]);

  const getLadyById = useCallback(
    async (id: string) => {
      if (id.length > 10) {
        openModal("INFORMATION_MODAL", {
          message:
            "ID too long. Please ensure it is no more than 10 characters.",
          statusReg: "fail",
        });
        return;
      }

      if (isNaN(Number(id)) || id === "") {
        openModal("INFORMATION_MODAL", {
          message: "You've entered incorrect data. ID should be numeric.",
          statusReg: "fail",
        });
        return;
      }

      try {
        const smallProfile = await SmallLadyProfileApi.getSmallProfile({
          objectId: id,
        });
        setLadyDtoState(smallProfile || undefined);
        setSelectedLadyId(smallProfile?.id || undefined);
      } catch (error: unknown) {
        if (error) {
          openModal("INFORMATION_MODAL", {
            message: `Lady with ID: ${id} was not found.`,
            statusReg: "fail",
          });
          setValue("ladyId", String(selectedLadyId));
          return;
        }
      }
    },
    [openModal, selectedLadyId, setValue],
  );

  useEffect(() => {
    if (!isLightVersion) {
      getLastContacts();
    }
  }, [isLightVersion, getLastContacts]);

  useEffect(() => {
    if (isLightVersion) return;
    const fetchSmallProfile = async () => {
      if (!selectedLadyId) return;

      try {
        const smallProfile = await SmallLadyProfileApi.getSmallProfile({
          objectId: String(selectedLadyId),
        });

        setLadyDtoState(smallProfile || undefined);
        setSelectedLadyId(smallProfile?.id || undefined);
      } catch (error: unknown) {
        openModal("INFORMATION_MODAL", {
          message: `Lady with ID: ${selectedLadyId} was not found.`,
          statusReg: "fail",
        });
      }
    };
    fetchSmallProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLadyId, isLightVersion]);
  const upload = useCallback(
    async (file: File, fileName: string) => {
      setIsLoading(true);
      let result = await UploadAttachApi.uploadAttach(
        file,
        fileName,
        StaticConst.EAttachedFileType.ORDINARY,
      );
      if (isError(result)) {
        openModal("INFORMATION_MODAL", {
          message:
            "Sorry, an error has occured. Could not attach the file. Reason: " +
            result.error,
          statusReg: "fail",
        });
        return;
      }

      if (isSuccess(result)) {
        const { id, fileNameUrl } = result;

        setAttaches((prevAttaches) => [...prevAttaches, { id, fileNameUrl }]);
        setIsLoading(false);
      }
    },
    [openModal],
  );
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);
  const uploadFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      const currentAttachmentsCount = attaches.length;
      const remainingSlots =
        StaticConst.MAX_ATTACHMENTS_NUMBER - currentAttachmentsCount;

      if (files.length > remainingSlots) {
        openModal("INFORMATION_MODAL", {
          message: `Sorry, but only ${StaticConst.MAX_ATTACHMENTS_NUMBER} attachments per letter are allowed.`,
          statusReg: "fail",
        });
      }

      const selectedFiles = Array.from(files).slice(0, remainingSlots);

      if (selectedFiles.length === 0) {
        return;
      }

      const invalidFormatFiles = selectedFiles.filter(
        (file) => !StaticConst.ALLOWED_FORMATS.includes(file.type),
      );

      if (invalidFormatFiles.length > 0) {
        const invalidFilesMessage = `Sorry, could not attach the following file: ${invalidFormatFiles
          .map((file) => file.name)
          .join(", ")}.
         Only PNG, JPG, and JPEG images are allowed.`;
        openModal("INFORMATION_MODAL", {
          message: invalidFilesMessage,
          statusReg: "fail",
        });
        return;
      }

      const selectedFileNames = selectedFiles.map((file) =>
        file.name.toLowerCase(),
      );
      const hasDuplicates = selectedFileNames.some((fileName) =>
        uploadedFileNames.includes(fileName),
      );

      if (hasDuplicates) {
        openModal("INFORMATION_MODAL", {
          message: "Sorry, looks like this file was attached before.",
          statusReg: "fail",
        });
        return;
      }

      const oversizedFiles = selectedFiles.filter(
        (file) => file.size > StaticConst.MAX_FILE_SIZE_MB * 1024 * 1024,
      );
      if (oversizedFiles.length > 0) {
        const oversizedFileNames = oversizedFiles
          .map((file) => file.name)
          .join(", ");
        const oversizedFilesMessage = `Sorry, could not attach the following file: ${oversizedFileNames}.
        Please make sure the file size is under ${StaticConst.MAX_FILE_SIZE_MB} MB.`;
        openModal("INFORMATION_MODAL", {
          message: oversizedFilesMessage,
          statusReg: "fail",
        });
        return;
      }

      setUploadedFileNames((prev) => [...prev, ...selectedFileNames]);

      selectedFiles.forEach((file) => {
        const fileName = file.name;
        if (!fileName) {
          openModal("INFORMATION_MODAL", {
            message: "You must specify a file name to be attached!",
            statusReg: "fail",
          });
          return;
        }

        const img = new Image();
        img.onload = function (event) {
          const imgElement = event.currentTarget as HTMLImageElement;
          const width = imgElement.width;
          const height = imgElement.height;
          const isValidSize =
            width <= StaticConst.MAX_ATTACHMENTS_WIDTH &&
            height <= StaticConst.MAX_ATTACHMENTS_HEIGHT;
          if (isValidSize) {
            upload(file, fileName)
              .then(() => {
                const previewUrl = URL.createObjectURL(file);
                setFilePreviews((prevPreviews) => [
                  ...prevPreviews,
                  previewUrl,
                ]);
              })
              .catch(() => {
                openModal("INFORMATION_MODAL", {
                  message: "File upload failed. Please try again.",
                  statusReg: "fail",
                });
              });
          } else {
            openModal("INFORMATION_MODAL", {
              message:
                "Image resolution too big. Please choose a smaller one (max " +
                StaticConst.MAX_ATTACHMENTS_WIDTH +
                "x" +
                StaticConst.MAX_ATTACHMENTS_HEIGHT +
                " pixels).",
              statusReg: "fail",
            });
          }
        };

        const reader = new FileReader();
        reader.onload = function (e) {
          const result = e.target?.result;
          if (typeof result === "string") {
            img.src = result;
          } else {
            openModal("INFORMATION_MODAL", {
              message: "Invalid image format.",
              statusReg: "fail",
            });
          }
        };
        reader.readAsDataURL(file);
      });
    },
    [attaches.length, openModal, upload, uploadedFileNames],
  );
  const IdTextFieldAdornment = useCallback(() => {
    return (
      <SvgIcon
        width={18}
        height={18}
        className="mr-1"
        onClick={() => getLadyById(getValues("ladyId"))}
      >
        <SearchIcon />
      </SvgIcon>
    );
  }, [getLadyById, getValues]);
  const delAttach = useCallback(
    (id?: number) => {
      const attachIds = id ? [id] : attaches.map((attach) => attach.id);
      RemoveAttachesApi.removeAttaches(attachIds);
    },
    [attaches],
  );

  const removeAttach = useCallback((index: number) => {
    setFilePreviews((prevPreviews) =>
      prevPreviews.filter((_, i) => i !== index),
    );
    setAttaches((prevAttaches) => prevAttaches.filter((_, i) => i !== index));
    setUploadedFileNames((prevAttaches) =>
      prevAttaches.filter((_, i) => i !== index),
    );
  }, []);

  const removePreview = (index: number) => {
    removeAttach(index);
    const currentId = attaches[index].id;
    delAttach(currentId);
  };
  const charCount = letterText.length;

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = event.target.value;
    const isTapEvent =
      event.nativeEvent instanceof InputEvent &&
      event.nativeEvent.inputType === "insertText";

    const showModal = (message: string) => {
      openModal("INFORMATION_MODAL", {
        message,
        statusReg: "fail",
      });
    };
    const cursorPosition = event.target.selectionStart;

    if (isTapEvent) {
      if (value.length > StaticConst.MAX_LETTER_TEXT_COUNT) {
        showModal(`Your message is too long.`);
      } else {
        setLetterText(value);

        if (selectedLadyId) {
          saveDraftForLady(selectedLadyId, value);
        }
      }
    } else {
      if (value.length > StaticConst.MAX_LETTER_TEXT_COUNT) {
        const beforeCursor = value.slice(0, cursorPosition);
        const afterCursor = value.slice(cursorPosition);

        const truncatedBeforeCursor = beforeCursor.slice(
          0,
          StaticConst.MAX_LETTER_TEXT_COUNT - afterCursor.length,
        );

        const newText = truncatedBeforeCursor + afterCursor;

        setLetterText(newText);
        if (selectedLadyId) {
          saveDraftForLady(selectedLadyId, newText);
        }

        showModal(
          `Your text is over the ${StaticConst.MAX_LETTER_TEXT_COUNT} character limit and has been shortened.`,
        );
      } else {
        setLetterText(value);

        if (selectedLadyId) {
          saveDraftForLady(selectedLadyId, value);
        }
      }
    }
  };
  const handleAttachmentClick = () => {
    if (attaches.length >= StaticConst.MAX_ATTACHMENTS_NUMBER) {
      openModal("INFORMATION_MODAL", {
        message: `Sorry, but only ${StaticConst.MAX_ATTACHMENTS_NUMBER} attachments per letter are allowed.`,
        statusReg: "fail",
      });
      return;
    }

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = StaticConst.ALLOWED_FORMATS.join(",");

    fileInput.onchange = (event) => {
      const changeEvent = event as unknown as ChangeEvent<HTMLInputElement>;
      const files = changeEvent.target.files;

      if (!files) return;

      uploadFile(changeEvent);
    };
    fileInput.click();
  };

  const combinedOptions = [
    ...(smallLadyProfiles || []),
    ...(ladyDtoState ? [ladyDtoState] : []),
  ];

  const uniqueOptions = Array.from(
    new Map(combinedOptions.map((item) => [item.id, item])).values(),
  );

  const handleTextChangeLadyId = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    newValue = newValue.replace(/[^0-9]/g, "").slice(0, 10);
    setSearchTerm(newValue as unknown as number);
    setValue("ladyId", newValue);
  };

  const handleSelectChange = (value: number) => {
    setSelectedLadyId(value);
    setValue("ladyId", String(value));
  };
  useEffect(() => {
    if (selectedLadyId !== searchTerm) {
      setSearchTerm(selectedLadyId);
      selectedLadyId && setValue("ladyId", String(selectedLadyId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLadyId, isLightVersion]);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter" && event.target instanceof HTMLElement) {
      const targetElement = event.target as
        | HTMLInputElement
        | HTMLTextAreaElement;

      if (targetElement.tagName === "TEXTAREA") {
        return;
      }
      event.preventDefault();
    }
  };
  const [, setDragCounter] = useState(0);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setDragCounter((prev) => prev + 1);
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setDragCounter((prev) => {
      if (prev - 1 === 0) {
        setIsDragOver(false);
      }
      return prev - 1;
    });
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setDragCounter(0);
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const fakeEvent = {
        target: {
          files: files,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      uploadFile(fakeEvent);
    }
  };
  const handleCloseClick = () => {
    delAttach();
    removeOne();
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="flex flex-col gap-4 w-full overflow-hidden box-border max-h-[754px] overflow-y-auto scroll-pb-20"
      sx={{
        ...scrollBarSxStyles,
        clipPath: "inset(7px 0 7px 0)",
      }}
    >
      {isDragOver && (
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-white bg-opacity-60 flex flex-col justify-center items-center text-grey1-700 text-lg font-bold z-10">
          <p>You can attach up to 2 photos per letter</p>
          <p>(PNG, JPG, JPEG, max 10MB each)</p>
        </div>
      )}
      <form onKeyDown={handleKeyDown} onSubmit={onSubmitHandler}>
        {isLoading && (
          <div className="flex justify-center items-center absolute inset-0 bg-white/80 z-10">
            <NextImage
              src={LoaderGif}
              alt="Loading..."
              width={40}
              height={40}
            />
          </div>
        )}
        {!isLightVersion && (
          <div className="p-4 pt-3 pb-0 sticky top-0 bg-white z-9">
            <ModalCloseCross onClose={handleCloseClick} className="ml-auto" />
            <h2 className="text-lg pb-3 text-grey1-800 font-[680] text-left">
              Write a message
            </h2>
            <div className="flex flex-col md:flex-row gap-4 w-full items-center pb-5">
              <CustomSelect
                value={selectedLadyId || ""}
                onChange={handleSelectChange}
                options={uniqueOptions}
              />
              {!downXs && (
                <p className="text-center text-grey1-200 text-base">or</p>
              )}

              <TextField
                {...register("ladyId")}
                InputProps={{
                  endAdornment: <IdTextFieldAdornment />,
                  sx: {
                    cursor: "pointer",
                    height: "52px !important",
                    borderRadius: "22px !important",
                    paddingLeft: "5px",
                  },
                }}
                inputProps={{ inputMode: "numeric" }}
                placeholder="Enter lady's ID"
                value={searchTerm}
                onChange={handleTextChangeLadyId}
                onBlur={() => searchTerm && getLadyById(String(searchTerm))}
                className="w-full md:w-1/2 h-100pxs"
              />
            </div>
            {!isLoading && (
              <div
                className={`flex gap-4 ${
                  isLightVersion ? "mb-0 pt-1" : "pb-2"
                }`}
              >
                {filePreviews.map((previewUrl, index) => (
                  <div key={index} className="relative inline-block">
                    <ModalCloseCross
                      absolute
                      className="absolute -top-2 -right-2 cursor-pointer text-gray-500 rounded-full bg-white"
                      onClose={() => removePreview(index)}
                    />

                    <NextImage
                      src={previewUrl}
                      alt={`Preview ${index}`}
                      width={40}
                      height={60}
                      className="rounded-[0.2rem] object-cover h-[60px]"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div
          className={`w-full ${
            isLightVersion
              ? "p-0 pt-2 flex flex-col h-full"
              : "pt-[19px] px-[20px]"
          } border-t-[1px] border-grey1-100 border-opacity-50`}
        >
          {isLightVersion ? (
            <div className="flex flex-col h-full mr-1">
              <div className="flex-grow mr-1">
                <AutoResizeTextArea
                  minRows={5}
                  register={register}
                  value={letterText || defaultLetterText}
                  onChange={handleTextChange}
                  fieldName="letterText"
                  placeholder="Enter your message…"
                />
              </div>

              <div className="flex items-center gap-4 pb-2 pt-1 sticky bottom-0 bg-white z-10  mt-2 justify-end">
                {charCount > StaticConst.MAX_LETTER_TEXT_COUNT - 1000 && (
                  <span className="text-sm text-grey1-500">
                    {charCount} / {StaticConst.MAX_LETTER_TEXT_COUNT} symbols
                  </span>
                )}

                <ButtonWithIcon
                  onClick={handleAttachmentClick}
                  iconColor="light-grey"
                  ariaLabel="Add attachment"
                  className="shadow"
                  Icon={FileIcon}
                />
                <Tooltip
                  arrow
                  title={
                    <span className="font-bold">10 COINS TO SEND A LETTER</span>
                  }
                  placement="top-end"
                  componentsProps={{
                    tooltip: {
                      className:
                        "bg-gradient-green-blue-green text-white text-[12px]",
                    },
                    arrow: {
                      className: "text-green",
                    },
                  }}
                >
                  <ButtonWithIcon
                    type="submit"
                    ariaLabel="Mail Me"
                    Icon={SendIcon}
                    className="bg-gradient-green-blue-green z-10"
                  />
                </Tooltip>
              </div>
            </div>
          ) : (
            <div className="flex flex-col w-full">
              <AutoResizeTextArea
                minRows={7}
                register={register}
                value={letterText}
                onChange={handleTextChange}
                fieldName="letterText"
                placeholder="Enter your message…"
              />
              <div className="flex flex-row py-4 justify-end gap-4 items-center w-full sticky bottom-0 bg-white z-9 shadow-lg pb-5">
                {charCount > StaticConst.MAX_LETTER_TEXT_COUNT - 1000 && (
                  <span className="text-sm text-grey1-500">
                    {charCount} / {StaticConst.MAX_LETTER_TEXT_COUNT} symbols
                  </span>
                )}

                <ButtonWithIcon
                  onClick={handleAttachmentClick}
                  iconColor="light-grey"
                  ariaLabel="Add attachment"
                  className="shadow"
                  Icon={FileIcon}
                />
                <Tooltip
                  arrow
                  title={
                    <span className="font-bold">10 COINS TO SEND A LETTER</span>
                  }
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      className:
                        "bg-gradient-green-blue-green text-white text-[12px]",
                    },
                    arrow: {
                      className: "text-green",
                    },
                  }}
                >
                  <Button
                    type="submit"
                    iconSize={24}
                    variant="primary"
                    startIcon={<SendIcon />}
                    onHoverVariant="gradient-flip"
                  >
                    Send message
                  </Button>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </form>
    </Box>
  );
};
